import { useCallback, useEffect, useRef, useState } from "react";
import { PauseCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ConfirmActionModal } from "../ConfirmActionModal";

interface PendingConfirm {
  /** Колко задържани поръчки засяга действието. */
  count: number;
  resolve: (proceed: boolean) => void;
}

/**
 * Питането „Задържани поръчки" преди „Закрий" и „Плати избраните" („Раздели и
 * плати"): закриването пуска задържаните поръчки в кухнята (BE ги освобождава в
 * транзакцията на закриването), затова сервитьорът го научава ПРЕДИ това.
 * [Отказ] спира действието; [Пусни и продължи] е обичайният поток — FE не
 * освобождава нищо сам.
 *
 * `confirmHeldOrders(count)` връща обещание: `true` — продължи (или няма какво
 * да се пита при `count === 0`), `false` — „Отказ". Модалът
 * (`heldOrdersModal`) се рендерира от извикващия.
 */
export const useHeldOrdersConfirm = () => {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);
  pendingRef.current = pending;
  // Броят остава в текста, докато модалът изчезва (анимацията).
  const lastShownRef = useRef<PendingConfirm | null>(null);
  if (pending) lastShownRef.current = pending;
  const count = (pending ?? lastShownRef.current)?.count ?? 1;

  // Затворен екран с висящо питане = „Отказ" (обещанието не остава да виси).
  useEffect(() => () => pendingRef.current?.resolve(false), []);

  const confirmHeldOrders = useCallback(
    (heldCount: number) =>
      heldCount === 0
        ? Promise.resolve(true)
        : new Promise<boolean>((resolve) => {
            pendingRef.current?.resolve(false);
            setPending({ count: heldCount, resolve });
          }),
    [],
  );

  const settle = (proceed: boolean) => {
    pending?.resolve(proceed);
    setPending(null);
  };

  const heldOrdersModal = (
    <ConfirmActionModal
      open={pending !== null}
      icon={<PauseCircleOutlined />}
      title={t("ordering.kitchenHold.confirmTitle")}
      message={t("ordering.kitchenHold.confirmMessage", { count })}
      question={t("ordering.kitchenHold.confirmQuestion", { count })}
      confirmLabel={t("ordering.kitchenHold.confirmContinue")}
      onCancel={() => settle(false)}
      onConfirm={() => settle(true)}
    />
  );

  return {
    confirmHeldOrders,
    heldOrdersModal,
    heldOrdersConfirmOpen: pending !== null,
  };
};
